import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { Logo } from "../Logo";
import { useLang } from "../../i18n";

export const Header = () => {
  const { ui, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const links = [
    { to: "/", label: ui.nav.home },
    { to: "/portofoliu", label: ui.nav.portfolio },
    { to: "/despre", label: ui.nav.about },
    { to: "/contact", label: ui.nav.contact },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className={`transition-all duration-500 ${
          scrolled ? "border-b border-white/10 bg-ink/80 backdrop-blur-md" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="container-pdr flex h-20 items-center justify-between">
          <Link
            to="/"
            data-testid="header-logo-link"
            aria-label="VOLTPDR home"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <Logo />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                    isActive ? "text-white" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-dot"
                        className="absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-glow-2 shadow-[0_0_10px_2px_rgba(112,214,255,0.8)]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 font-mono text-xs uppercase tracking-widest text-white/80 transition-colors hover:border-glow-2/50 hover:text-white"
            >
              <Languages className="h-3.5 w-3.5" />
              {lang}
            </button>
            {pathname !== "/contact" && (
              <Link
                to="/contact"
                data-testid="header-quote-btn"
                className="btn-glow hidden rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-ink transition-transform duration-300 hover:scale-[1.03] sm:inline-flex glow-ring"
              >
                {ui.nav.quote}
              </Link>
            )}
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="border-b border-white/10 bg-ink/95 backdrop-blur-md lg:hidden"
          >
            <nav className="container-pdr flex flex-col gap-1 py-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium ${
                      isActive ? "bg-white/5 text-white" : "text-white/70"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              {pathname !== "/contact" && (
                <Link to="/contact" className="mt-2 rounded-xl bg-brand px-4 py-3 text-center font-semibold text-ink">
                  {ui.nav.quote}
                </Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
